<template>
	<!-- App bar -->
	<v-app-bar :elevation="1" color="primary" id="app-bar">
		<v-app-bar-nav-icon @click.stop="manageRailState" />

		<a href="/" class="brand-logo d-flex align-center text-decoration-none">
			<v-img :src="getImageUrl('logo-white')" height="32" width="120" contain />
		</a>

		<div class="ml-2 ga-1 d-flex flex-column">
			<v-chip size="x-small" pill class="chip rounded-xl text-uppercase">{{ env.NODE_ENV }}</v-chip>
			<v-chip class="text-caption" pill size="x-small"
				>{{ vueVersion }}<template v-if="webApiVersion"> | {{ webApiVersion }}</template></v-chip
			>
		</div>

		<v-spacer />

		<v-text-field id="searchInput" ref="searchBar" @update:model-value="onSearchInput" @keydown.ctrl.f="onSearchFocus" type="search" variant="outlined" density="compact" 
		hide-details single-line :clear-icon="icons.mdiCloseCircle" clearable persistent-clear :prepend-inner-icon="icons.mdiMagnify" class="mr-4">
			<template #append-inner>
				<v-chip class="text-body-small text-no-wrap rounded" density="compact">ctrl + f</v-chip>
			</template>
		</v-text-field>
		<v-spacer />
	</v-app-bar>

	<!-- Navigation drawer -->
	<v-navigation-drawer :rail="railState" permanent expand-on-hover>
		<v-list-item lines="two" class="pa-4 edit-profile" @click="openProfileDialog">
			<template #prepend>
				<v-avatar color="#0e4f80" class="no-profile-picture">
					<span>{{ initials }}</span>
				</v-avatar>
			</template>

			<v-list-item-title class="font-weight-semibold">{{ fullName }}</v-list-item-title>
			<v-list-item-subtitle class="text-no-wrap">{{ subtitle }}</v-list-item-subtitle>
		</v-list-item>

		<v-divider />

		<v-list nav density="comfortable">
			<v-list-item to="/" :prepend-icon="icons.mdiHomeOutline" :title="$t('menu.home')" />

			<v-list-item to="/Customers" :prepend-icon="icons.mdiAccountGroupOutline" :title="$t('menu.customers')" />

			<v-list-item v-if="!hasDistributor" to="/Distributors" :prepend-icon="icons.mdiHexagonMultipleOutline" :title="$t('menu.distributors')" />

			<v-list-item to="/Users" :prepend-icon="icons.mdiAccountOutline" :title="$t('menu.users')" />

			<v-divider class="my-2" />

			<v-list-subheader>{{ $t("menu.server") }}</v-list-subheader>

			<v-list-item to="/Jobs" :prepend-icon="icons.mdiRocketLaunchOutline" :title="$t('menu.jobs')" />

			<v-list-item v-show="false" to="/Configuration" :prepend-icon="icons.mdiCogOutline" :title="$t('menu.configurations')" />

			<v-divider class="my-2" />

			<v-list-item :prepend-icon="icons.mdiLogout" :title="$t('menu.logOff')" @click="logOff" />
		</v-list>
	</v-navigation-drawer>

	<!-- Main content -->
	<v-main class="d-flex flex-fill flex-column page-transition-container">
		<v-progress-linear v-show="progress > 0 && progress < 100" :model-value="progress" color="tertiary"></v-progress-linear>
		<router-view v-slot="{ Component }">
			<transition :name="$route.meta.transition || 'page-fade'" mode="out-in">
				<keep-alive exclude="ViewHome,ViewUsers,ViewManageCustomer">
					<component :is="Component" :key="$route.path"></component>
				</keep-alive>
			</transition>
		</router-view>
		<Loader ref="loader" class="wrapper-logo-container" />
	</v-main>

	<!-- Profile dialog -->
	<v-dialog v-model="profileDialog" h-33 transition="dialog-bottom-transition" class="w-33">
		<v-card class="w-100 overflow-visible pt-16">
			<div class="d-flex justify-center pa-6">
				<v-avatar size="205" color="primary" class="position-absolute dialog-picture elevation-3" border="border-color xl">
					<span class="initials text-display-large">{{ initials }}</span>
				</v-avatar>
			</div>
			<v-card-text>
				<v-text-field class="ma-auto rounded-text-field" v-model="profileName" :label="$t('field.name')" :prepend-inner-icon="icons.mdiText" icon-color="primary" density="compact" variant="outlined" disabled maxlength="25" />
			</v-card-text>
			<v-divider />
			<v-card-text>
				<v-select v-model="selectedLanguage" w-75 density="compact" :items="languages" item-title="name" item-value="code" :label="$t('field.language')" :prepend-inner-icon="icons.mdiWeb" icon-color="primary" variant="outlined" required />

				<v-text-field v-model="password" density="compact" :label="$t('field.password')" :prepend-inner-icon="icons.mdiLockOutline" icon-color="primary" variant="outlined" type="password" required :error="passwordMismatch" :error-messages="passwordMismatch ? [$t('toast.loginPasswordMismatch')] : []" />
			</v-card-text>

			<v-divider />

			<v-card-actions>
				<v-spacer />
				<v-btn color="error" variant="text" @click="profileDialog = false" density="default">
					{{ $t("action.cancel") }}
				</v-btn>
				<v-btn color="primary" variant="text" :disabled="!isProfileValid" @click="saveUserInfo" density="default">
					{{ $t("action.ok") }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script setup>
	import { computed, getCurrentInstance, inject, provide, onMounted, onUnmounted, ref, watch, nextTick } from "vue";
	import { useI18n } from "vue-i18n";
	import locales from "../components/json/locales.json";
	import Loader from "@/components/Loader.vue";
	import { toast } from "vue3-toastify";
	import { useControlStore } from "@/stores/controlStore";
	import { useLogStore } from "@/stores/logStore";
	import { useUserStore } from "@/stores/userStore";

	import { mdiHome, mdiHomeOutline, mdiAccount, mdiAccountOutline, mdiAccountGroup, mdiAccountGroupOutline, mdiHexagonMultipleOutline, mdiRocketLaunchOutline, mdiCogOutline, mdiLogout, mdiText, mdiWeb, mdiLockOutline, mdiMagnify, mdiCloseCircle } from "@mdi/js";

	const { locale } = useI18n();
	const navBarInstance = getCurrentInstance();
	const controlStore = useControlStore();
	const logStore = useLogStore();
	const userStore = useUserStore();
	const loader = ref(null);

	// #region INJECT
	const urlWebAPI = inject("urlWebAPI");
	const vueVersion = inject("vueVersion");
	const webApiVersion = inject("webApiVersion");
	const translate = inject("translate");
	// #endregion

	const env = import.meta.env;

	const languages = computed(() => getLanguages());
	const hasDistributor = userStore.getUser().hasDistributor;

	const fullName = ref("");
	const subtitle = ref("");
	const initials = ref("");
	const railState = ref(false);
	const profileDialog = ref(false);
	const profileName = ref("");
	const password = ref("");
	const selectedLanguage = ref("");
	const passwordMismatch = ref(false);
	const searchBar = ref(null);

	const icons = {
		mdiHome,
		mdiHomeOutline,
		mdiAccount,
		mdiAccountOutline,
		mdiAccountGroup,
		mdiAccountGroupOutline,
		mdiHexagonMultipleOutline,
		mdiRocketLaunchOutline,
		mdiCogOutline,
		mdiLogout,
		mdiText,
		mdiWeb,
		mdiLockOutline,
		mdiMagnify,
		mdiCloseCircle
	};

	const isProfileValid = computed(() => password.value !== "" && selectedLanguage.value !== "");

	const progress = ref(0);
	let progressRunId = 0;

	watch(profileDialog, (isOpen) => {
		if (isOpen) onProfileDialogOpened();
	});

	onMounted(() => {
		getVersion();
		loadCurrentUser();
		railState.value = JSON.parse(localStorage.getItem("railState")) ?? railState.value;
	});

	onUnmounted(() => {
		localStorage.setItem("railState", railState.value);
	});

	function manageRailState() {
		railState.value = !railState.value;
		localStorage.setItem("railState", railState.value);
	}

	function getVersion() {
		// WEB API VERSION
		fetch(urlWebAPI + "GetVersion", {
			method: "GET",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
			},
		})
			.then((result) => result.json())
			.then((version) => {
				webApiVersion.value = version;
			})
			.catch((error) => {
				logStore.log(error, "error");
				toast.error("Error during executing GetVersion data.");
			});
	}

	function getLanguages() {
		var localeCodes = Object.keys(locales);
		var languages = [];
		for (var i = 0; i < localeCodes.length; i++) {
			var localeCode = localeCodes[i];

			languages.push({
				name: translate(localeCode.toLowerCase(), "global"),
				code: localeCode.toUpperCase(),
			});
		}

		return languages;
	}

	function getImageUrl(name, extension = "png") {
		return new URL(`../assets/images/${name}.${extension}`, import.meta.url).href;
	}

	function loadCurrentUser() {
		const user = userStore.getUser();

		fullName.value = `${user.firstName} ${user.lastName}`;
		initials.value = (user.firstName.substr(0, 1) + user.lastName.substr(0, 1)).toUpperCase();
		subtitle.value = user.email;
	}

	function openProfileDialog() {
		profileDialog.value = true;
	}

	function onProfileDialogOpened() {
		const user = userStore.getUser();

		profileName.value = `${user.firstName} ${user.lastName}`;
		selectedLanguage.value = user.locale === null ? locale.value.toUpperCase() : user.locale.toUpperCase();
		password.value = "";
		passwordMismatch.value = false;
	}

	function onSearchInput(search) {
		var gridsApi = controlStore.grids;
		gridsApi.forEach((grid) => {
			// Convert special accented characters to their non-accented counterparts
			if (search !== null){
				search = search.normalize("NFD").replace(/\p{Diacritic}/gu, "");
			}
			if (grid != null) grid.setGridOption("quickFilterText", search);
		});
	}

	function onSearchFocus() {
		searchInput.focus();
	}

	function saveUserInfo() {
		passwordMismatch.value = false;

		var userInfo = userStore.getUser();
		userInfo.locale = selectedLanguage.value.toLowerCase();
		userInfo.password = password.value;

		let data = {
			token: userStore.getUser().token,
			customerReference: "Externis",
			userInfo: userInfo,
		};

		$.postData(urlWebAPI + "SaveUserInfo", data)
			.then((data) => {
				if (data != null) {
					toast.success(translate("userInfoWellSaved", "toast"), {
						theme: "colored",
					});
					userStore.setLocale(navBarInstance.proxy, userInfo.locale, true);
					profileDialog.value = false;
				}
				// USER NOT FOUND
				else {
					passwordMismatch.value = true;
				}
			})
			.catch((error) => {
				userStore.setLocale(navBarInstance.proxy, selectedLanguage.value.toLowerCase(), true);
				logStore.log(error, "error");
				toast.error("Error during executing SaveCustomerDetail data.", { theme: "colored" });
			});
	}

	function logOff() {
		userStore.logOff();
	}

	async function startProgress() {
		const runId = ++progressRunId;
		const start = Date.now();
		progress.value = 0;
		// Soft cap: near-linear (LIMIT / SECONDS_TO_LIMIT per second) then bends smoothly towards LIMIT.
		// SHARPNESS: higher = later and sharper slowdown (8 => slows around 80-90).
		const LIMIT = 99;
		const SECONDS_TO_LIMIT = 11;
		const SHARPNESS = 8;
		while (runId === progressRunId) {
			const x = (Date.now() - start) / 1000 / SECONDS_TO_LIMIT;
			progress.value = (LIMIT * x) / Math.pow(1 + Math.pow(x, SHARPNESS), 1 / SHARPNESS);
			await new Promise((resolve) => requestAnimationFrame(resolve));
		}
	}

	function stopProgress() {
		const runId = ++progressRunId;
		progress.value = 100;
		// The first frame renders the bar at 100, the second one hides it (progress >= 101).
		requestAnimationFrame(() => {
			if (runId === progressRunId) progress.value = 99;
			requestAnimationFrame(() => {
				if (runId === progressRunId) progress.value++;
			});
		});
	}

	function startLoading() {
		loader.value.hide = "";
		setTimeout(() => {
			loader.value.loading = "is-loading";
		}, 750);
	}

	function stopLoading() {
		loader.value.loading = "";
		setTimeout(() => {
			loader.value.hide = "hide";
		}, 250);
	}

	provide("startProgress", startProgress);
	provide("stopProgress", stopProgress);
	provide("startLoading", startLoading);
	provide("stopLoading", stopLoading);
</script>

<style scoped>
.brand-logo {
	color: inherit;
}

.environment {
	font-weight: 600;
}
.chip {
	width: fit-content;
}

.page-transition-container {
	overflow-x: hidden;
}
</style>
